///
/// EarthBrowser
///
/// Created by Matt Giger on 9/5/2026
/// Copyright (c) 2026 Geodata Labs
///

import { DesignColors } from "@/earthbrowser/design-system/DesignColors"
import { DrapedPolygonGeometry } from "@/earthbrowser/entities/geometries/DrapedPolygonGeometry"
import { PolylineNode } from "@/earthbrowser/entities/Polyline/PolylineNode"
import { SimpleNode } from "@/earthbrowser/entities/SimpleNode/SimpleNode"
import { cSurfaceOverlayDepthBias } from "@/earthbrowser/entities/surfaceDepthBias"
import { geographic } from "@/earthbrowser/utils/geographic"
import { NodeBase } from "@stovokor/engine/NodeBase"
import { trig } from "@stovokor/math/math"
import { vec2 } from "@stovokor/math/vector"
import { type StovokorContext } from "@stovokor/StovokorContext"

// The outline floats slightly above the fill so it always wins the depth
// test against its own polygon
const cOutlineLiftKm = 0.15

const cOutlineWidth = 1.5
const cOutlineWidthFocused = 2.5

// The feed's cone rings arrive with 1000+ vertices; the outline doesn't
// need more than a few hundred to look identical
const cMaxOutlinePoints = 400

interface GeoJSONPolygon {
	type: "Polygon"
	coordinates: number[][][]
}

interface GeoJSONMultiPolygon {
	type: "MultiPolygon"
	coordinates: number[][][][]
}

export type StormConeGeoJSON = GeoJSONPolygon | GeoJSONMultiPolygon

/**
 * Forecast uncertainty cone for one storm: translucent fill draped on the
 * globe plus a crisp outline. The fill uses DrapedPolygonGeometry, which
 * densifies the interior before triangulating — a cone can span 20+ degrees
 * of arc, and a flat-triangulated polygon that size cuts dozens of km below
 * the surface. Fills are best-effort: a degenerate ring falls back to
 * outline-only rather than dropping the cone.
 */
export class StormConeNode extends NodeBase {
	private readonly fills: SimpleNode[] = []
	private readonly outlines: PolylineNode[] = []
	private focused = false

	constructor(context: StovokorContext, geometry: StormConeGeoJSON, altitudeKm: number) {
		super()
		const outlineAltitude = altitudeKm + cOutlineLiftKm

		const polygons: number[][][][] =
			geometry.type === "Polygon" ? [geometry.coordinates] : geometry.coordinates

		for (const rings of polygons) {
			const outer = rings[0]
			if (!outer || outer.length < 4) continue

			try {
				const ringVec2 = outer.map(([lon, lat]) => new vec2(lon, lat))
				const fillGeometry = new DrapedPolygonGeometry(context, ringVec2, altitudeKm)
				const fill = new SimpleNode(context, fillGeometry, this.fillColor(), { polygonOffset: cSurfaceOverlayDepthBias })
				this.fills.push(fill)
				this.addNode(fill)
			} catch {
				// Degenerate/self-intersecting ring: keep the outline
			}

			const step = Math.max(1, Math.floor(outer.length / cMaxOutlinePoints))
			const positions = []
			for (let i = 0; i < outer.length; i += step) {
				const [lon, lat] = outer[i]
				positions.push(new geographic(lon * trig.rad, lat * trig.rad, outlineAltitude).cartesian)
			}
			if (positions.length < 2) continue

			const outline = new PolylineNode(context, {
				positions,
				width: cOutlineWidth,
				color: this.outlineColor(),
				loop: true,
				depthWrite: false,
				polygonOffset: cSurfaceOverlayDepthBias,
			})
			this.outlines.push(outline)
			this.addNode(outline)
		}
	}

	/** Brighten fill and thicken outline while the storm is selected */
	public setFocused(focused: boolean): void {
		if (this.focused === focused) return
		this.focused = focused

		const fillColor = this.fillColor()
		for (const fill of this.fills) {
			fill.color = fillColor
		}
		const outlineColor = this.outlineColor()
		const width = this.focused ? cOutlineWidthFocused : cOutlineWidth
		for (const outline of this.outlines) {
			outline.updateColor(outlineColor)
			outline.updateWidth(width)
		}
	}

	private fillColor() {
		return this.focused ? DesignColors.STORM_CONE_FILL_FOCUSED : DesignColors.STORM_CONE_FILL
	}

	private outlineColor() {
		return this.focused ? DesignColors.STORM_CONE_OUTLINE_FOCUSED : DesignColors.STORM_CONE_OUTLINE
	}
}
